import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  InputAdornment,
  Button,
  TextField,
  Box,
  Typography,
  Avatar,
  Stack,
  Grid,
  Alert,
  IconButton,
  Badge,
  MenuItem,
  CircularProgress,
} from "@mui/material";
import {
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  LocationOn as LocationOnIcon,
  AccountCircle as AccountCircleIcon,
} from "@mui/icons-material";
import CloseIcon from "@mui/icons-material/Close";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";
import phoneCodes from "../../../utils/static/phoneCodes";

const EditProfileDialog = ({ open, onClose, user, onUpdate }) => {
  const parsePhoneNumber = (rawNumber, phoneCodes) => {
    if (!rawNumber) return { code: phoneCodes[0].code, number: "" };

    // If legacy format starts with +
    if (rawNumber.startsWith("+")) {
      // Try to match against known phone codes
      const match = phoneCodes.find((pc) => rawNumber.startsWith(pc.code));
      if (match) {
        return {
          code: match.code,
          number: rawNumber.replace(match.code, ""),
        };
      }
      // Fallback if no match
      return { code: phoneCodes[0].code, number: rawNumber };
    }

    // If already separated format
    return { code: user?.phoneCode || phoneCodes[0].code, number: rawNumber };
  };
  const { code, number } = parsePhoneNumber(user?.phoneNumber, phoneCodes);

  const [formData, setFormData] = useState({
    name: user?.name || "",
    email: user?.email || "",
    phoneCode: user?.phoneCode || code,
    phoneNumber: number,
    address: user?.address || "",
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: "",
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = "Name is required";
    }

    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Please enter a valid email address";
    }

    if (
      formData.phoneNumber &&
      !/^\+?[\d\s\-\(\)]+$/.test(formData.phoneNumber)
    ) {
      newErrors.phoneNumber = "Please enter a valid phone number";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    onUpdate(formData);
    setIsSubmitting(false);
  };

  const handleCancel = () => {
    setFormData({
      name: user?.name || "",
      email: user?.email || "",
      phoneNumber: user?.phoneNumber || "",
      address: user?.address || "",
    });
    setErrors({});
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleCancel}
      maxWidth='md'
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 4,
          minHeight: "65vh",
          background: "linear-gradient(145deg, #ffffff 0%, #f8fafc 100%)",
          boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
        },
      }}
    >
      <Box
        sx={{
          px: 3,
          py: 2.5,
          display: "flex",
          alignItems: "center",
          borderBottom: "1px solid",
          borderColor: "divider",
          position: "relative",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", mr: 2 }}>
          <Badge
            overlap='circular'
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            badgeContent={
              <Avatar
                sx={{
                  width: 24,
                  height: 24,
                  bgcolor: "primary.main",
                  border: "2px solid white",
                }}
              >
                {/* <EditIcon sx={{ fontSize: 12 }} /> */}
              </Avatar>
            }
          >
            <Avatar
              sx={{
                width: 64,
                height: 64,
                fontSize: 24,
                backdropFilter: "blur(10px)",
                fontWeight: 700,
                bgcolor: (theme) => theme.palette.primary.main + "22", // primary alpha
                color: "primary.main",
              }}
            >
              {user?.name?.charAt(0).toUpperCase() || "U"}
            </Avatar>
          </Badge>
        </Box>
        <Stack spacing={0.4} sx={{ flexGrow: 1 }}>
          <Typography variant='h6' fontWeight={700} color='text.primary'>
            Profile Update
          </Typography>
          <Typography variant='caption' color='text.secondary'>
            Update your account information
          </Typography>
        </Stack>
        <IconButton
          onClick={handleCancel}
          size='small'
          sx={{
            color: "text.secondary",
            position: "absolute",
            top: 18,
            right: 12,
          }}
        >
          <CloseIcon />
        </IconButton>
      </Box>

      <DialogContent sx={{ p: 1 }}>
        {errors.submit && (
          <Box sx={{ p: 3, pb: 0 }}>
            <Alert
              severity='error'
              sx={{
                borderRadius: 2,
                "& .MuiAlert-icon": {
                  fontSize: 20,
                },
              }}
            >
              {errors.submit}
            </Alert>
          </Box>
        )}

        <Box sx={{ p: 3 }}>
          {/* Personal Information Section */}
          <Box>
            <Typography
              variant='h6'
              sx={{
                fontWeight: 600,
                mb: 2,
                color: "text.primary",
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <PersonIcon color='primary' />
              Personal Information
            </Typography>

            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label='Full Name'
                  value={formData.name}
                  onChange={(e) => handleInputChange("name", e.target.value)}
                  error={!!errors.name}
                  helperText={errors.name}
                  variant='outlined'
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                      transition: "all 0.2s ease",
                      "&:hover": {
                        boxShadow: "0 0 0 2px rgba(25, 118, 210, 0.1)",
                      },
                      "&.Mui-focused": {
                        boxShadow: "0 0 0 2px rgba(25, 118, 210, 0.2)",
                      },
                    },
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position='start'>
                        <AccountCircleIcon color='action' />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label='Email Address'
                  type='email'
                  value={formData.email}
                  onChange={(e) => handleInputChange("email", e.target.value)}
                  error={!!errors.email}
                  helperText={errors.email || "Email cannot be changed"}
                  variant='outlined'
                  disabled
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                      bgcolor: "grey.50",
                    },
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position='start'>
                        <EmailIcon color='action' />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <Typography
                  variant='body2'
                  color='text.secondary'
                  sx={{ mb: 1, fontWeight: 500 }}
                >
                  Phone Number
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 1.5,
                  }}
                >
                  <TextField
                    select
                    value={formData.phoneCode || "+65"}
                    onChange={(e) =>
                      handleInputChange("phoneCode", e.target.value)
                    }
                    error={!!errors.phoneCode}
                    helperText={errors.phoneCode}
                    sx={{
                      width: "140px",
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 2,
                        bgcolor: "#fafafa",
                        transition: "all 0.2s ease",
                        "&:hover": {
                          boxShadow: "0 0 0 2px rgba(25, 118, 210, 0.1)",
                        },
                        "&.Mui-focused": {
                          boxShadow: "0 0 0 2px rgba(25, 118, 210, 0.2)",
                        },
                      },
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position='start'>
                          <PhoneIcon color='action' sx={{ fontSize: 18 }} />
                        </InputAdornment>
                      ),
                    }}
                  >
                    {phoneCodes.map((option) => (
                      <MenuItem key={option.code} value={option.code}>
                        {option.code} ({option.country})
                      </MenuItem>
                    ))}
                  </TextField>

                  <TextField
                    fullWidth
                    value={formData.phoneNumber}
                    onChange={(e) =>
                      handleInputChange("phoneNumber", e.target.value)
                    }
                    error={!!errors.phoneNumber}
                    helperText={errors.phoneNumber}
                    variant='outlined'
                    placeholder='1234 5678'
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 2,
                        bgcolor: "#fafafa",
                        transition: "all 0.2s ease",
                        "&:hover": {
                          boxShadow: "0 0 0 2px rgba(25, 118, 210, 0.1)",
                        },
                        "&.Mui-focused": {
                          boxShadow: "0 0 0 2px rgba(25, 118, 210, 0.2)",
                        },
                      },
                    }}
                  />
                </Box>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label='Address'
                  value={formData.address}
                  onChange={(e) => handleInputChange("address", e.target.value)}
                  multiline
                  rows={3}
                  variant='outlined'
                  placeholder='Enter your address (optional)'
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                      transition: "all 0.2s ease",
                      "&:hover": {
                        boxShadow: "0 0 0 2px rgba(25, 118, 210, 0.1)",
                      },
                      "&.Mui-focused": {
                        boxShadow: "0 0 0 2px rgba(25, 118, 210, 0.2)",
                      },
                    },
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment
                        position='start'
                        sx={{ alignSelf: "flex-start", mt: 1 }}
                      >
                        <LocationOnIcon color='action' />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
            </Grid>
          </Box>
        </Box>
      </DialogContent>

      {/* Enhanced Footer */}
      <Box
        sx={{
          p: 3,
          borderTop: "1px solid",
          borderColor: "grey.200",
          background: "linear-gradient(145deg, #ffffff 0%, #f8fafc 100%)",
        }}
      >
        <Stack direction='row' spacing={2} justifyContent='flex-end'>
          <Button
            onClick={handleCancel}
            startIcon={<CancelIcon />}
            variant='outlined'
            disabled={isSubmitting}
            sx={{
              borderRadius: 2,
              px: 3,
              py: 1,
              fontWeight: 600,
              textTransform: "none",
              borderColor: "grey.300",
              color: "text.secondary",
              "&:hover": {
                borderColor: "grey.400",
                bgcolor: "grey.50",
                transform: "translateY(-1px)",
              },
              transition: "all 0.2s ease",
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            startIcon={
              isSubmitting ? (
                <CircularProgress size={16} color='inherit' />
              ) : (
                <SaveIcon />
              )
            }
            variant='contained'
            disabled={isSubmitting}
            sx={{
              px: 4,
              py: 1,
              fontWeight: 600,
              textTransform: "none",
              minWidth: 140,
              boxShadow: "0 4px 12px rgba(102, 126, 234, 0.4)",
              "&:hover": {
                background:
                  "linear-gradient(135deg, #9575CD 0%, #603F97 50%, #4F337A 100%)",
                boxShadow: "0 6px 16px rgba(102, 126, 234, 0.5)",
                transform: "translateY(-2px)",
              },
              "&:disabled": {
                background: "grey.300",
                boxShadow: "none",
              },
              transition: "all 0.2s ease",
            }}
          >
            {isSubmitting ? "Saving..." : "Save Changes"}
          </Button>
        </Stack>
      </Box>
    </Dialog>
  );
};

export default EditProfileDialog;
